import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ReportService } from '../../services/report.service';
import swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ReportComponent } from '../../report.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { LanguageService } from 'src/app/services/language.service';
import { ActivatedRoute } from '@angular/router';
import { StylometricStateService } from '../../services/stylometric-state.service';
import { Subscription } from 'rxjs';

export interface Manipulation {
  includedCharacters: { character: string; count: number }[];
  excludedReplacedCharacters: any[];
  characters: any[];
  isExcluded: boolean;
}

export interface IsManipulated {
  isManipulated: boolean;
  isCharReplacement: boolean;
  isHiddenText: boolean;
}

export interface Translated {
  lang: string;
  similarity: number;
  percentage: string;
  source: any;
  isExcluded: boolean;
  comment: string
  sentence: string
  secondarySources: any
}

export interface Data {
  submissionId: string,
  text: string;
  comment: string;
  sentence: string;
  source: any;
  id: number;
  isOriginal: boolean;
  isTranslated: boolean;
  isManipulated: IsManipulated;
  isExcluded: boolean;
  isAi: boolean;
  similarity: number;
  page: number;
  manipulations: Manipulation[];
  translated: Translated[];
  secondarySources: any,
  model_name: string,
  section_key: string,
  original?: string,
  ai_generated?: string,
  similarity_percentage?: number,
  ai_percentage?: number,
  complete_percentage?: number,
  stylometrics?: {
    original?: {
      features: {
        lexical: Record<string, number>;
        syntactic: Record<string, number>;
        structural: Record<string, number>;
        readability: Record<string, number>;
      }
    };
    ai_generated?: {
      features: {
        lexical: Record<string, number>;
        syntactic: Record<string, number>;
        structural: Record<string, number>;
        readability: Record<string, number>;
      }
    };
    flagged_features?: string[];
    stylistic_discrepancy?: string;
  }
}

// Interface for stylometric metrics
export interface StylometricMetric {
  name: string;
  value: number;
}

@Component({
  selector: 'app-sentence-information',
  templateUrl: './sentence-information.component.html',
  styleUrls: ['./sentence-information.component.scss']
})
export class SentenceInformationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('exclusionInput') exclusionInput: ElementRef;
  @ViewChild('sentenceInfoTitle', { static: false }) sentenceInfoTitle!: ElementRef;
  toggledLang: string | null = null
  data: Data | null = null;
  isOriginal = false;
  isTranslated = false;
  isAi = false;
  isManipulation = false;
  correctData: any;

  sentence: string = '';
  source: string = '';
  analysisType: string;
  sentenceManipulation: string;

  result: any;
  excluded_result: any;
  selectionState: boolean[] = [];
  excludedSelectionState: boolean[] = [];
  modalType = '';

  showInsideModal = false;
  includeClickCount = 0;
  manipulationAction = '';
  excludeClickCount: number = 0;
  commentValue: string = '';
  isEditable: boolean = false;
  showExclude: boolean = false;

  isOriginalExcluded: boolean = false;
  isTranslationExcluded: boolean = false;
  isOriginalLanguage: boolean;
  showCommentButtons: boolean;
  isChangeSource = false;
  selectedSentenceSources: any;
  selectedSource: string;
  changeSourceClickCount = 0;

  formattedText!: string;
  formattedSentence!: string;

  manipulationType: string;
  @Input() context_similarity: any;
  @Input() exactMatchThreshold: number
  @Input() contextSimilarityThreshold: number
  @Input() submission: any

  selectedTransltaionCode;
  translatedObj: any;

  // Tabs for manipulation
  isOnCharReplacement = true;
  isOnHiddenText = false;

  excludedCharReplacement: boolean;
  excludedHiddenText: boolean;

  @Input() canEdit;
  excludeButtonText = this.translate.instant('new_entries.exclude_matching_sentence')
  isOnIA: boolean;

  isIncludedSelected: boolean = true;
  isExcludedSelected: boolean = true;

  selectAllIncluded: boolean = false;
  selectAllExcluded: boolean = false;

  isProjectSynapseActive: boolean = false;

  // Stylometric table properties
  isStylometricTableExpanded: boolean = false;
  selectedCategory: string = 'lexical';
  stylometricCategories = [
    { id: 'lexical', name: 'Lexical' },
    { id: 'syntactic', name: 'Syntactic' },
    { id: 'structural', name: 'Structural' },
    { id: 'readability', name: 'Readability' }
  ];

  isSideBySideVisible = false;
  twoColumns = false;
  private subscriptions = new Array<Subscription>();

  isSourceHistoryExpanded = false;

  availableAIModels: { value: string; label: string; title: string; fileUrl: string; comparisonData: any; }[] = [];
  currentAIModel: string = '';

  // New properties for stylometric table
  stylometricThresholds: Record<string, number> = {};

  constructor(
    private reportService: ReportService,
    private toastr: ToastrService,
    public translate: TranslateService,
    private reportComponent: ReportComponent,
    private spinnerService: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private languageService: LanguageService, // dont delete this its used on HTML
    private acRoute: ActivatedRoute,
    private stylometricState: StylometricStateService,

  ) { }

  ngOnInit(): void {
    if (this.acRoute.snapshot.params.clientName) {
      this.isOnIA = true
    } else {
      this.isOnIA = false
    }

    // Subscribe to Project Synapse active state
    this.subscriptions.push(
      this.stylometricState.isProjectSynapseActive$.subscribe(isActive => {
        this.isProjectSynapseActive = isActive;

        // If Project Synapse becomes active, initialize the current model
        if (isActive) {
          // Get the current model from the shared service
          const currentModel = this.stylometricState.getCurrentAIModel();
          if (currentModel) {
            this.currentAIModel = currentModel.value;

            // Initialize available models from the state service
            this.availableAIModels = this.stylometricState.getAvailableAIModels() || [];

            // If we have data, update it with the current model
            if (this.data) {
              this.updateDataWithCurrentModel();

              // Initialize stylometric table if data is available
              if (this.data.stylometrics) {
                this.initializeStylometricTable();
              }
            }
          }
        }
      })
    );

    this.subscriptions.push(
      this.reportService.selectedTransltaionCodeObject.subscribe((data) => {
        if (data !== null) {
          this.selectedTransltaionCode = data
          if (this.data && this.data.translated && this.data.translated.length > 0) {
            this.translatedObj = this.data.translated.find(lang => lang.lang === this.selectedTransltaionCode) || this.data.translated[0];
            this.commentValue = this.translatedObj.comment
          }
        }
      })
    );
    this.subscriptions.push(
      this.reportService.selectAnalysisTypeObject.subscribe((data: string) => {
        this.analysisType = data;
      })
    );
    if (this.analysisType !== 'none') {
      if (this.analysisType == 'manipulation') {
        this.isOriginal = false
        this.isTranslated = false
        this.isManipulation = true
        this.analysisType = 'manipulation'
      } else {
        this.reportService.selectLanguageObject.subscribe((data) => {
          if (data.type === 'Original') {
            this.isOriginal = true
            this.isTranslated = false
            this.analysisType = 'Original'
          } else if (data.type === 'Translated') {
            this.isOriginal = false
            this.isTranslated = true
            this.analysisType = 'Translated'

          }
        })
      }
    }


    this.subscriptions.push(
      this.reportService.sentenceInformationObject.subscribe((data: any) => {
        if (data) {
          this.data = data;
          console.log(this.data, 'data');
          // CHECK FOR PROJECT SYNAPSE
          if (data.isProjectSynapse) {
            this.isProjectSynapseActive = true;
            // Force update of AI generated text if we have a current model
            if (this.currentAIModel && this.availableAIModels.length > 0) {
              const selectedModel = this.availableAIModels.find(model => model.value === this.currentAIModel);
              if (selectedModel && selectedModel.comparisonData) {
                // Direct update of simulated text
                const aiData = selectedModel.comparisonData;
                if (aiData.ai_generated !== undefined) {
                  this.data.ai_generated = aiData.ai_generated;
                } else if (aiData.text !== undefined) {
                  this.data.ai_generated = aiData.text;
                } else if (aiData.generated_text !== undefined) {
                  this.data.ai_generated = aiData.generated_text;
                }

                // Run full update
                this.updateDataWithCurrentModel();

                // Force change detection
                setTimeout(() => {
                  this.cdr.detectChanges();
                }, 0);
              }
            }
          }

          if(this.data.secondarySources){
            if(this.data.secondarySources.length > 0){
             this.data.secondarySources = this.data.secondarySources.filter((source: any) => source.isExcluded === false);
            }
           }
          if (!this.data?.isManipulated?.isCharReplacement) {
            if (this.data?.manipulations?.[0]?.excludedReplacedCharacters?.length > 0) {
              this.excludedCharReplacement = true;
            } else {
              this.excludedCharReplacement = false;
            }
          } else {
            this.excludedCharReplacement = false;
          }


          if (!this.data?.isManipulated?.isHiddenText) {
            if ((this.data?.manipulations?.[1]?.characters.length > 0 && this.data?.manipulations?.[1]?.isExcluded)) {
              this.excludedHiddenText = true;
            } else {
              this.excludedHiddenText = false;
            }
          } else {
            this.excludedHiddenText = false;
          }


          if (this.data?.translated && this.data?.translated?.length > 0) {
            this.translatedObj = data.translated.find(lang => lang.lang === this.selectedTransltaionCode) || data.translated[0];
            this.toggledLang = this.translatedObj?.lang;
            this.reportService.selectedTransltaionCodeSubject.next(this.toggledLang)
          }

          this.isOriginalExcluded = this.data.isExcluded
          if (this.data?.translated?.length > 0) {
            this.isTranslationExcluded = this.translatedObj?.isExcluded
          }
          if (this.data?.manipulations?.[0]?.includedCharacters) {
            this.result = this.data?.manipulations?.[0]?.includedCharacters;
          }
          if (this.data?.manipulations?.[0]?.excludedReplacedCharacters) {
            this.excluded_result = this.data?.manipulations?.[0]?.excludedReplacedCharacters;
          }

          if (this.result && this.result.length > 0) {
            this.selectionState = new Array(this.result.length).fill(false);
          }
          if (this.excluded_result && this.excluded_result.length > 0) {
            this.excludedSelectionState = new Array(this.excluded_result.length).fill(false);
          }
          if (this.data?.isManipulated?.isCharReplacement === true || this.excludedCharReplacement) {
            this.data.isManipulated.isManipulated = true;
            this.isOnCharReplacement = true;
            this.isOnHiddenText = false; // Ensure the other one is not opened
          } else if (this.data?.isManipulated?.isHiddenText === true || this.excludedHiddenText) {
            this.data.isManipulated.isManipulated = true;
            this.isOnCharReplacement = false; // Ensure the other one is not opened
            this.isOnHiddenText = true;
          } else {
            this.isOnCharReplacement = false;
            this.isOnHiddenText = false;
          }


          if (this.data?.isManipulated?.isCharReplacement === false && this.data?.isManipulated?.isHiddenText) {
            this.reportService.selectManipulationTypeSubject.next('hiddenText')
          } else if (this.data?.isManipulated?.isCharReplacement && this.data?.isManipulated?.isHiddenText === false) {
            this.reportService.selectManipulationTypeSubject.next('charReplacement')
          }

          if (this.data?.isManipulated?.isHiddenText || this.excludedHiddenText) {
            this.formattedText = this.formatTextWithWhiteCharacters(this.data.text, this.data?.manipulations?.[1]?.characters ?? this.data?.manipulations?.[0]?.characters);
          }

          // if (this.data.source.contextSimilarity) {
          //   this.formattedSentence = this.formatSentenceWithContext();
          // }

          this.updateTabs(data);
          this.cdr.detectChanges();
        } else {
          this.clearTabs();
        }
      })
    );

    this.subscriptions.push(
      this.reportService.selectManipulationTypeObject.subscribe((data: any) => {
        this.manipulationType = data;

        if (this.analysisType === 'manipulation' && this.manipulationType === 'hiddenText') {

          this.isOnCharReplacement = false;
          this.isOnHiddenText = true;
        }
      })
    );

    this.subscriptions.push(
      this.stylometricState.availableAIModels$.subscribe(models => {
        this.availableAIModels = models;
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.sentenceInfoTitle) {
      this.sentenceInfoTitle.nativeElement.focus();
    }

    // Load available AI models for Project Synapse
    this.subscriptions.push(
      this.stylometricState.availableAIModels$.subscribe(models => {
        if (models && models.length > 0) {
          this.availableAIModels = models;

          // If we have models and project synapse is active, update current model
          if (this.isProjectSynapseActive) {
            // First check if a model is already selected in the service
            const currentServiceModel = this.stylometricState.getCurrentAIModel();

            if (currentServiceModel) {
              // Update to use the model from the service
              this.currentAIModel = currentServiceModel.value;
            } else if (!this.currentAIModel && models.length > 0) {
              // No model is selected, use the first one
              this.currentAIModel = models[0].value;

              // Update the service with this model
              const selectedModel = models.find(model => model.value === this.currentAIModel);
              if (selectedModel) {
                this.stylometricState.setSelectedAIModel(selectedModel);
              }
            }
          }

          // If we have data and models, ensure the data is updated with the current model
          if (this.data && this.isProjectSynapseActive && this.currentAIModel) {

            // Force immediate update of simulated text
            const currentModel = models.find(model => model.value === this.currentAIModel);
            if (currentModel && currentModel.comparisonData) {
              // Direct update of simulated text
              const aiData = currentModel.comparisonData;
              if (aiData.ai_generated !== undefined) {
                this.data.ai_generated = aiData.ai_generated;
              } else if (aiData.text !== undefined) {
                this.data.ai_generated = aiData.text;
              } else if (aiData.generated_text !== undefined) {
                this.data.ai_generated = aiData.generated_text;
              }
            }

            // Full update
            this.updateDataWithCurrentModel();

            // Force change detection
            this.cdr.detectChanges();
          }
        }
      })
    );

    // Subscribe to model changes from the service
    this.subscriptions.push(
      this.stylometricState.selectedAIModel$.subscribe(model => {
        if (model && this.isProjectSynapseActive) {
          // Only update if the model has actually changed
          if (this.currentAIModel !== model.value) {
            this.currentAIModel = model.value;

            // Update the UI with the new model's data
            if (this.data) {
              // Direct update of simulated text
              if (model.comparisonData) {
                const aiData = model.comparisonData;
                if (aiData.ai_generated !== undefined) {
                  this.data.ai_generated = aiData.ai_generated;
                } else if (aiData.text !== undefined) {
                  this.data.ai_generated = aiData.text;
                } else if (aiData.generated_text !== undefined) {
                  this.data.ai_generated = aiData.generated_text;
                }
              }

              // Full update
              this.updateDataWithCurrentModel();

              // Force change detection
              this.cdr.detectChanges();
            }
          }
        }
      })
    );
  }

  formatSentenceWithContext(sentence?: string): string {
    let splitParts
    if (sentence && sentence?.includes('class="context">')) {
      return splitParts
    } else {
      if (sentence) {
        splitParts = sentence.split(/(<em>.*?<\/em>)/g);
      } else {
        splitParts = this.isOriginal ? this.data.sentence.split(/(<em>.*?<\/em>)/g) : this.translatedObj.source.sourceSentence.split(/(<em>.*?<\/em>)/g);
      }
    }
    let formattedSentence = '';

    splitParts.forEach(part => {
      if (part.startsWith('<em>')) {
        formattedSentence += part;
      } else {
        // Split the part by whitespace to preserve spaces and wrap only non-whitespace parts
        const words = part.split(/(\s+)/);
        words.forEach(word => {
          if (word.trim().length > 0) {
            formattedSentence += `<span class="context">${word}</span>`;
          } else {
            formattedSentence += word; // Preserve whitespace
          }
        });
      }
    });
    this.formattedText = formattedSentence;
    this.cdr.detectChanges();
    return formattedSentence;
  }

  clearSentenceInformation() {
    // Store whether Project Synapse was active before clearing
    const wasProjectSynapseActive = this.isProjectSynapseActive;

    // Store the current language information to preserve it
    const currentLanguageType = this.isTranslated ? 'Translated' : 'Original';
    const currentLanguageCode = this.isTranslated && this.toggledLang ? this.toggledLang : null;

    // Close side-by-side view if it's open
    this.reportService.isSideBySideVisibleSubject.next(false);

    // Clear sentence information
    this.reportService.sentenceInformation(null, '', null, wasProjectSynapseActive);

    // If Project Synapse was active, ensure it stays active
    if (wasProjectSynapseActive) {
      // Lock Project Synapse state to prevent any changes
      this.stylometricState.lockProjectSynapseState();

      // Force Project Synapse to stay active with a small delay to ensure proper sequence
      setTimeout(() => {
        // Force Project Synapse to stay active
        this.stylometricState.forceProjectSynapseState(true);

        // Ensure the analysis type is set to projectSynapse
        this.reportService.selectAnalysisTypeSubject.next('projectSynapse');

        // Keep Project Synapse locked for a longer period to prevent any delayed actions
        setTimeout(() => {
          this.stylometricState.unlockProjectSynapseState();
        }, 1000); // Lock for a full second to prevent any delayed actions
      }, 10);
    }

    // If we were viewing a translated language, make sure it remains selected
    if (currentLanguageType === 'Translated' && currentLanguageCode) {
      setTimeout(() => {
        // Store language information for proper restoration
        const translatedLanguage = this.data?.translated?.find(t => t.lang === currentLanguageCode);
        let languageName = '';

        // Try to get the language name - first from our local data, then from the language service
        if (translatedLanguage) {
          // Get the language name from our local data
          languageName = this.languageService.getLanguageName(currentLanguageCode);
        } else {
          // Fallback to just using the language code
          languageName = this.languageService.getLanguageName(currentLanguageCode) || currentLanguageCode;
        }

        // Keep the current language selection
        this.reportService.selectLanguageSubject.next({
          language: languageName,
          type: 'Translated'
        });

        // Keep the translated language code
        this.reportService.selectedTransltaionCodeSubject.next(currentLanguageCode);

        // Store the expanded language code and type to ensure the language bar expands correctly
        this.reportService.expandedLanguageSubject.next({
          code: currentLanguageCode,
          type: 'Translated'
        });
      }, 100);
    }

    // Reset local state
    this.data = null;
    this.isOriginal = false;
    this.isTranslated = false;
    this.isAi = false;
    this.isManipulation = false;
    this.showExclude = false;
    this.isEditable = false;
    this.showCommentButtons = false;
  }

  updateTabs(data: Data) {
    this.isOriginal = data?.isOriginal;
    this.isTranslated = data?.isTranslated;
    this.isAi = data?.isAi;
    this.isManipulation = data?.isManipulated?.isManipulated || this.excludedCharReplacement || this.excludedHiddenText;


    if (this.analysisType == 'none') {
      if (this.isOriginal) {
        this.activeTab('original');
      } else if (this.isTranslated) {
        this.activeTab('translated');
      } else if (this.isAi) {
        this.activeTab('ai');
      } else if (this.isManipulation) {
        this.activeTab('manipulation');
      }
    } else if (this.analysisType == 'Original' || this.analysisType == 'Translated') {
      this.activeTab(this.analysisType.toLowerCase());
    } else if (this.analysisType == 'ai') {
      if (this.isAi) {
        this.activeTab('ai');
      }
    } else if (this.analysisType == 'manipulation') {


      if (this.isManipulation) {
        this.activeTab('manipulation');
      }

      if (this.manipulationType === 'hiddenText') {
        this.isOnCharReplacement = false;
        this.isOnHiddenText = true;
      }
    }

  }

  clearTabs() {
    this.isOriginal = false;
    this.isTranslated = false;
    this.isAi = false;
    this.isManipulation = false;
    this.sentence = '';
    this.source = '';
    this.sentenceManipulation = '';
  }

  activeTab(tab: string) {
    this.isChangeSource = false
    this.changeSourceClickCount = 0
    this.isOriginal = tab === 'original';
    this.isTranslated = tab === 'translated';
    this.isAi = tab === 'ai';
    this.isManipulation = tab === 'manipulation';

    if (this.data) {
        switch (tab) {
            case 'original':
                this.sentence = this.data.sentence;
                if (this.data.source?.contextSimilarity && this.context_similarity && this.data.source?.percentage < 60 && parseInt(this.data.source?.contextSimilarityPercentage) > this.contextSimilarityThreshold) {
                    this.sentence = this.formatSentenceWithContext();
                }
                this.source = this.isOriginal ? this.data?.source?.url : this.translatedObj?.source?.url;
                this.isOriginalLanguage = true
                this.commentValue = this.data.comment;
                this.updateShowExclude();
                if (this.showExclude) {
                    this.isEditable = false;
                    this.showCommentButtons = false;
                }
                break;

            case 'translated':
                this.sentence = this.translatedObj?.sentence;
                if (this.analysisType === 'none') {
                    for (let translated of this.data.translated) {
                        if (translated?.source?.contextSimilarity && this.context_similarity && translated?.source?.percentage < 60 && translated?.source?.contextSimilarityPercentage > this.contextSimilarityThreshold) {
                            this.sentence = this.formatSentenceWithContext(translated?.source?.sentence);
                            translated.sentence = this.sentence
                        }
                    }
                } else {
                    this.sentence = this.translatedObj?.sentence;
                    if (this.translatedObj?.source?.contextSimilarity && this.context_similarity && this.translatedObj?.source?.percentage < 60 && this.translatedObj?.source?.contextSimilarityPercentage > this.contextSimilarityThreshold) {
                        this.sentence = this.formatSentenceWithContext(this.sentence)
                    }
                }
                this.source = this.translatedObj?.source?.url;
                this.isOriginalLanguage = false
                this.commentValue = this.translatedObj?.comment;
                this.updateShowExclude();
                if (this.showExclude) {
                    this.isEditable = false;
                    this.showCommentButtons = false;
                }
                break;

            case 'ai':
                this.showExclude = false;
                break;

            case 'manipulation':
                this.showExclude = false;
                this.generateManipulatedSentence(this.data);
                this.formattedText = this.formatTextWithWhiteCharacters(
                    this.data.text,
                    this.data?.manipulations?.[1]?.characters ??
                    this.data?.manipulations?.[0]?.characters
                );
                break;
        }
    }
  }

  generateManipulatedSentence(data: Data) {
    if (data?.manipulations && data.manipulations.length > 0) {
      // Safely access and map the characters with null checks
      const includedCharacters = data.manipulations[0]?.includedCharacters?.map(c => c?.character)?.filter(Boolean) || [];
      const excludedCharacters = data.manipulations[0]?.excludedReplacedCharacters?.map(c => c?.character)?.filter(Boolean) || [];
      let manipulatedSentence = data.text || '';

      if (includedCharacters.length > 0) {
        includedCharacters.forEach(char => {
          if (char) {  // Add null check for character
            const regExp = new RegExp(`(${char})(?![^<]*>)`, 'g');
            manipulatedSentence = manipulatedSentence.replace(regExp, '<span class="highlight-red">$1</span>');
          }
        });

        excludedCharacters.forEach(char => {
          if (char) {  // Add null check for character
            const regExp = new RegExp(`(${char})(?![^<]*>)`, 'g');
            manipulatedSentence = manipulatedSentence.replace(regExp, '<span class="highlight-grey">$1</span>');
          }
        });
      } else if (excludedCharacters.length > 0) {
        includedCharacters.forEach(char => {
          if (char) {  // Add null check for character
            const regExp = new RegExp(`(${char})(?![^<]*>)`, 'g');
            manipulatedSentence = manipulatedSentence.replace(regExp, '<span class="highlight-red">$1</span>');
          }
        });

        excludedCharacters.forEach(char => {
          if (char) {  // Add null check for character
            const regExp = new RegExp(`(${char})(?![^<]*>)`, 'g');
            manipulatedSentence = manipulatedSentence.replace(regExp, '<span class="highlight-grey">$1</span>');
          }
        });
      }

      this.sentenceManipulation = manipulatedSentence;
      this.cdr.detectChanges();
    } else {
      this.sentenceManipulation = data?.sentence || '';
      this.cdr.detectChanges();
    }
  }

  toggleSelectAll(event: any) {
    const checked = event.target.checked;
    if (this.modalType == 'exclude') {
      this.selectionState = this.selectionState.fill(checked);
    } else if (this.modalType == 'include') {
      this.excludedSelectionState = this.excludedSelectionState.fill(checked);
    }
  }

  selectionIsDisabled() {
    if (this.analysisType === 'manipulation') {
      // For manipulation analysis, check selection state
      return !this.selectionState.some(char => char);
    } else {
      // For none analysis type, check if there are any includedCharacters left
      const hasIncludedChars = this.data?.manipulations?.[0]?.includedCharacters?.length > 0;
      return !hasIncludedChars; // disable if no included characters
    }
  }

  excludedSelectionIsDisabled(): boolean {
    if (this.analysisType === 'manipulation') {
      // For manipulation analysis, check selection state
      return !this.excludedSelectionState.some(state => state);
    } else {
      // For none analysis type, check if there are any excludedCharacters
      const hasExcludedChars = this.data?.manipulations?.[0]?.excludedReplacedCharacters?.length > 0;
      return !hasExcludedChars; // disable if no excluded characters
    }
  }

  onCheckboxChange(group: 'included' | 'excluded', index: number) {
    if (group === 'included') {
      this.isIncludedSelected = this.selectionState.some(state => state);
      this.selectAllIncluded = this.selectionState.every(state => state);
      if (this.isIncludedSelected) {
        this.isExcludedSelected = false;
        this.selectAllExcluded = false;
        if (this.excludedSelectionState?.length > 0) {
          this.excludedSelectionState = this.excludedSelectionState.fill(false);
        }
      }
    } else {
      this.isExcludedSelected = this.excludedSelectionState.some(state => state);
      this.selectAllExcluded = this.excludedSelectionState.every(state => state);
      if (this.isExcludedSelected) {
        this.isIncludedSelected = false;
        this.selectAllIncluded = false;
        if (this.selectionState?.length > 0) {
          this.selectionState = this.selectionState.fill(false);
        }
      }
    }

    if (!this.isIncludedSelected && !this.isExcludedSelected) {
      this.isIncludedSelected = true;
      this.isExcludedSelected = true;
    }
  }

  onSelectAllChange(type: 'included' | 'excluded'): void {
    if (type === 'included') {
      if (this.selectionState?.length > 0) {
        this.selectionState = this.selectionState.map(() => this.selectAllIncluded);
      }
      this.isIncludedSelected = this.selectAllIncluded;
      if (this.selectAllIncluded) {
        this.isExcludedSelected = false;
        this.selectAllExcluded = false;
        if (this.excludedSelectionState?.length > 0) {
          this.excludedSelectionState = this.excludedSelectionState.fill(false);
        }
      }
    } else {
      if (this.excludedSelectionState?.length > 0) {
        this.excludedSelectionState = this.excludedSelectionState.map(() => this.selectAllExcluded);
      }
      this.isExcludedSelected = this.selectAllExcluded;
      if (this.selectAllExcluded) {
        this.isIncludedSelected = false;
        this.selectAllIncluded = false;
        if (this.selectionState?.length > 0) {
          this.selectionState = this.selectionState.fill(false);
        }
      }
    }

    if (!this.selectAllIncluded && !this.selectAllExcluded) {
      this.isIncludedSelected = true;
      this.isExcludedSelected = true;
    }
  }


  // Function to include character replacements
  includeCharacters() {
    this.manipulationAction = 'include';
    let toggledCharacters;
    if (!this.excludedSelectionState || this.excludedSelectionState.every(state => !state)) {
      toggledCharacters = this.data.manipulations[0].excludedReplacedCharacters;
    } else {
      toggledCharacters = this.excluded_result.filter((char: any, index: any) => this.excludedSelectionState[index]);
    }

    if (toggledCharacters.length === 0) {
      toggledCharacters = this.data.manipulations[0].excludedReplacedCharacters;
    }

    swal.fire({
      title: this.translate.instant('notifications.are_you_sure_you_want_to_include_the_selected_characters'),
      showDenyButton: true,
      confirmButtonText: this.translate.instant('general.yes'),
      confirmButtonColor: '#087f23',
      denyButtonColor: '#c62828',
      denyButtonText: this.translate.instant('general.no'),
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinnerService.show();
        this.reportService
          .toggleReplacedCharacters(this.data.submissionId, toggledCharacters, 2)
          .pipe()
          .subscribe((data: any) => {
            this.reportComponent.getS3Json(data?.presignedUrlJson);
            this.toastr.success(this.translate.instant('notifications.character_included_successfully'));
            this.resetState()
            this.reportService.sentenceInformation(null, '', null);
            this.spinnerService.hide();
          })
      } else if (result.isDenied) {
        swal.fire(this.translate.instant('notifications.characters_are_not_included'), '', 'info');
        this.resetState()
        this.reportService.sentenceInformation(null, '', null);
        this.spinnerService.hide();
      }
    });
  }

  // Function to exclude character replacements
  excludeCharacters() {
    this.manipulationAction = 'exclude';

    // Get all characters if none are specifically selected
    let toggledCharacters;
    if (!this.selectionState || this.selectionState.every(state => !state)) {
      toggledCharacters = this.data.manipulations[0].includedCharacters;
    } else {
      toggledCharacters = this.result.filter((char: any, index: any) => this.selectionState[index]);
    }

    if (toggledCharacters.length === 0) {
      toggledCharacters = this.data.manipulations[0].includedCharacters;
    }

    swal.fire({
      title: this.translate.instant("notifications.are_you_sure_you_want_to_exclude_the_selected_characters"),
      showDenyButton: true,
      confirmButtonText: this.translate.instant("general.yes"),
      denyButtonText: this.translate.instant("general.no"),
      confirmButtonColor: '#087f23',
      denyButtonColor: '#c62828',
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinnerService.show();
        this.reportService
          .toggleReplacedCharacters(this.data.submissionId, toggledCharacters, 1)
          .pipe()
          .subscribe((data: any) => {
            this.reportComponent.getS3Json(data?.presignedUrlJson);
            this.toastr.success(this.translate.instant("notifications.character_excluded_successfully"));
            this.resetState();
            this.reportService.sentenceInformation(null, '', null);
            this.spinnerService.hide();
          })
      } else if (result.isDenied) {
        swal.fire(this.translate.instant("notifications.characters_are_not_excluded"), '', 'info');
        this.resetState();
      }
    });
  }

  // isButtonDisabled(): boolean {
  //   return !this.selectionState.some(state => state);
  // }

  resetState() {
    this.showInsideModal = false;
    this.excludeClickCount = 0;
    this.includeClickCount = 0;
    this.showExclude = false;
    this.isEditable = false;
    this.showCommentButtons = false;
  }

  excludeSentence(lang?) {
    this.excludeClickCount++;
    let isExcluded;
    if (this.isOriginal) {
      isExcluded = this.data.isExcluded;
    } else if (this.isTranslated) {
      isExcluded = this.translatedObj?.isExcluded;
    }

    if (this.excludeClickCount === (isExcluded ? 1 : 2)) {
      this.sendExcludeActionsRequest(this.data, isExcluded, lang);
    } else {
      this.showExclude = !this.showExclude;
      if (!isExcluded) {
        this.isEditable = true;
        this.showCommentButtons = false;

        requestAnimationFrame(() => {
          if (this.exclusionInput?.nativeElement) {
            this.exclusionInput.nativeElement.focus();
          }
        });
      }
    }
  }

  sendExcludeActionsRequest(data: any, isExcluded: boolean, lang?) {



    this.spinnerService.show();
    const serviceMethod = isExcluded ? 'includeSentence' : 'excludeSentence';
    const successMessage = isExcluded ? 'Include Sentence' : 'Exclude Sentence';
    const infoMessage = isExcluded ? 'Sentence included successfully!' : 'Sentence excluded successfully!';

    const language = this.isOriginalLanguage ? 'original' : this.toggledLang ? this.toggledLang : lang;

    this.reportService[serviceMethod](
      data.submissionId,
      data.id,
      language,
      this.commentValue,
      this.isOnIA
    ).subscribe((data: { infoToSend: string[] }) => {
      this.toastr.success(infoMessage, successMessage);
      this.spinnerService.hide();
      this.reportService.newS3JsonSubject.next(data[0]);
      this.isEditable = isExcluded;
      this.showExclude = !isExcluded;
      this.includeClickCount = 0;
      this.excludeClickCount = 0;
      // Reset the button text and other related states
      if (isExcluded) {
        this.data.isExcluded = false;
        if (this.isTranslated && this.translatedObj) {
          this.translatedObj.isExcluded = false;
        }
      } else {
        this.data.isExcluded = true;
        if (this.isTranslated && this.translatedObj) {
          this.translatedObj.isExcluded = true;
        }
      }
      // this.clearSentenceInformation()
    });
  }

  commentAction(action: 'update' | 'discard') {
    this.spinnerService.show();
    if (action === 'update') {
      this.isEditable = true;
      const language = this.isOriginalLanguage ? 'original' : this.toggledLang ? this.toggledLang : this.selectedTransltaionCode;
      this.reportService.updateComment(
        this.commentValue,
        this.data.submissionId,
        parseInt(this.data.id.toString()),
        language,
      ).subscribe((data: any) => {
        this.reportComponent.getS3Json(data?.presignedUrl);
        this.toastr.success(data?.message);
        // Update the local data object based on whether it's original or translated
        // if (this.isOriginal) {
        //   this.data.comment = this.commentValue;
        // } else if (this.isTranslated) {
        //   // Update both translatedObj and the corresponding entry in data.translated
        //   if (this.translatedObj) {
        //     this.translatedObj.comment = this.commentValue;
        //   }

        //   // Find and update the comment in the data.translated array
        //   const translationIndex = this.data.translated.findIndex(t => t.lang === this.selectedTransltaionCode);
        //   if (translationIndex !== -1) {
        //     this.data.translated[translationIndex].comment = this.commentValue;
        //   }
        // }

        this.isEditable = false;
        this.showCommentButtons = false;
        this.spinnerService.hide();
        this.reportComponent.getS3Json(data.presignedUrlJson);
      });
    } else {
      this.spinnerService.hide();
      // Reset to the original comment value based on whether it's original or translated
      if (this.isOriginal) {
        this.commentValue = this.data.comment;
      } else if (this.isTranslated && this.translatedObj) {
        this.commentValue = this.translatedObj.comment;
      }
      this.isEditable = false;
      this.showCommentButtons = false;
    }
  }


  get excludeButtonAriaLabel(): string {
    return this.correctData?.isExcluded ? this.translate.instant('new_entries.include_sentence_as_match') : this.translate.instant('new_entries.exclude_sentence_as_match');
  }

  get manipulationExcludeButtonText(): string {
    return this.data.manipulations[1]?.isExcluded ? this.translate.instant('new_entries.include_matching_sentence') : this.translate.instant('new_entries.exclude_matching_sentence');
  }

  formatTextWithWhiteCharacters(text: string, characters: any[]): string {
    // Create an array from the text for easier manipulation
    let textArray = text.split('');
    let nonSpaceIndex = 0;

    for (let i = 0; i < textArray.length; i++) {
      if (textArray[i] !== ' ' && (characters && characters.length > 0)) {
        characters.forEach(char => {
          if (char.isWhite && nonSpaceIndex === char.index && textArray[i] === char.character) {
            if (this.data?.manipulations?.[1]?.isExcluded ?? this.data?.manipulations?.[0]?.isExcluded) {
              textArray[i] = `<span class="whiteChar excluded" style="background: rgb(93, 174, 255) !important; color: #000 !important">${textArray[i]}</span>`;
            } else {
              textArray[i] = `<span class="whiteChar" style="background: rgb(93, 174, 255) !important; color: #000 !important">${textArray[i]}</span>`;
            }
          }
        });
        nonSpaceIndex++;
      }
    }
    // Join the array back into a string
    return textArray.join('');
  }

  getClassificationText(data) {
    this.correctData = data;
    if (this.analysisType === 'Translated') {
        this.correctData = this.translatedObj;
    } else if (data.lang) {
        const currentTranslation = this.data.translated.find(t => t.lang === data.lang);

        this.correctData = {
            similarity: currentTranslation?.source?.percentage || 0,
            source: {
                averageDistance: currentTranslation?.source?.averageDistance || 0,
                contextSimilarityPercentage: currentTranslation?.source?.contextSimilarityPercentage || 0,
                isExcluded: currentTranslation?.source?.isExcluded,
                type: currentTranslation?.source?.type
            },
            isExcluded: currentTranslation?.isExcluded || false,
        };
    }

    const similarity = this.correctData?.similarity || this.correctData?.source?.percentage || 0;
    const proximity = (this.correctData?.source?.averageDistance || 0) / 100;
    const contextSimilarity = parseFloat(this.correctData?.source?.contextSimilarityPercentage) || 0;
    const sourceType = this.correctData?.source?.type;

    // Update button text without modifying showExclude
    if (this.correctData?.isExcluded || this.correctData?.source?.isExcluded) {
        this.excludeButtonText = this.translate.instant('new_entries.include_matching_sentence');
    } else {
        this.excludeButtonText = this.translate.instant('new_entries.exclude_matching_sentence');
    }
    // First check for excluded and citation cases (these remain unchanged)
    if (this.correctData?.isExcluded || this.correctData?.source?.isExcluded) {
        return this.translate.instant('report.excluded');
    } else if (data?.isCitation) {
        return this.translate.instant('new_entries.cited_text');
    }
    // Then check if we can use the new logic based on primarySource.type
    else if (sourceType !== undefined) {
        // Use the new logic based on primarySource.type
        if (sourceType === 1) {
            return this.translate.instant('report.exact_match');
        } else if (sourceType === 2) {
            return this.translate.instant('report.possibly_altered_text');
        } else if (sourceType === 3) {
            return this.translate.instant('new_entries.contextual_similarity');
        }
    }
    // Only use the old logic as a fallback when primarySource.type doesn't exist
    else {
        if (similarity === 100 || (similarity >= (this.submission?.sentence_thresholds?.exactMatchThreshold || 80) && proximity < 2.00)) {
            return this.translate.instant('report.exact_match');
        } else if ((similarity >= (this.submission.sentence_thresholds.alteredTextThreshold || 60) && similarity < (this.submission.sentence_thresholds.exactMatchThreshold || 80) && proximity < 2.00) ||
            (similarity >= (this.submission.sentence_thresholds.alteredTextThreshold || 60) && proximity >= 2.00 && proximity <= 3.00 && contextSimilarity > (this.submission.sentence_thresholds.contextualSimilaritiesThreshold || 80))) {
            return this.translate.instant('report.possibly_altered_text');
        } else if (this.context_similarity ? contextSimilarity > (this.submission.sentence_thresholds.contextualSimilaritiesThreshold || 80) : false) {
            return this.translate.instant('new_entries.contextual_similarity');
        }
    }

    return ''; // Default return if no conditions are met
  }

  // New method to update showExclude state
  private updateShowExclude() {
    if (!this.isManipulation && !this.isAi) {
        this.showExclude = (this.isOriginal && this.data?.isExcluded) ||
                          (this.isTranslated && this.translatedObj?.isExcluded);
    } else {
        this.showExclude = false;
    }
  }

  getBgColor(data) {
    if (!data) return '';

    this.correctData = data;

    if (this.analysisType === 'Translated') {
        this.correctData = this.translatedObj;
    } else if (data.lang) {
        this.correctData = {
            similarity: data.source?.percentage || 0,
            source: {
                averageDistance: data.source?.averageDistance || 0,
                contextSimilarityPercentage: data.source?.contextSimilarityPercentage || 0,
                isExcluded: data.source?.isExcluded,
                type: data.source?.type
            },
            isExcluded: data.isExcluded,
            isCitation: data.isCitation
        };
    }

    const similarity = this.correctData?.similarity || this.correctData?.source?.percentage || 0;
    const proximity = (this.correctData?.source?.averageDistance || 0) / 100;
    const contextSimilarity = parseFloat(this.correctData?.source?.contextSimilarityPercentage) || 0;
    const sourceType = this.correctData?.source?.type;

    // First check for excluded and citation cases (these remain unchanged)
    if (this.correctData?.isExcluded || this.correctData?.source?.isExcluded) {
        return '#d9d9d9'; // Gray color for Excluded
    } else if (data?.isCitation) {
        return '#BDF4DD'; // Green color for Citation
    }
    // Then check if we can use the new logic based on primarySource.type
    else if (sourceType !== undefined) {
        // Use the new logic based on primarySource.type
        if (sourceType === 1) {
            return '#F9C3D5'; // Red color for Exact Match
        } else if (sourceType === 2) {
            return '#B3D9FF'; // Blue color for Altered text
        } else if (sourceType === 3) {
            return '#FEE5B3'; // Yellow color for Context similarity
        }
    }
    // Only use the old logic as a fallback when primarySource.type doesn't exist
    else {
        if (similarity === 100 || (similarity >= (this.submission?.sentence_thresholds?.exactMatchThreshold || 80) && proximity < 2.00)) {
            return '#F9C3D5'; // Red color for Exact Match
        } else if ((similarity >= (this.submission.sentence_thresholds.alteredTextThreshold || 60) && similarity < (this.submission.sentence_thresholds.exactMatchThreshold || 80) && proximity < 2.00) ||
        (similarity >= (this.submission.sentence_thresholds.alteredTextThreshold || 60) && proximity >= 2.00 && proximity <= 3.00 && contextSimilarity > (this.submission.sentence_thresholds.contextualSimilaritiesThreshold || 80))) {
            return '#B3D9FF'; // Blue color for Altered text
        } else if (this.context_similarity ? contextSimilarity > (this.submission.sentence_thresholds.contextualSimilaritiesThreshold || 80) : false) {
            return '#FEE5B3'; // Yellow color for Context similarity
        }
    }

    return ''; // Default return if no conditions are met
  }

  toggleHiddenText(id: any) {
    swal.fire({
      title: this.translate.instant('notifications.are_you_sure'),
      text: this.data?.manipulations?.[1]?.isExcluded ?? this.data?.manipulations?.[0]?.isExcluded ? this.translate.instant('new_entries.you_are_about_to_include_this_sentence') : this.translate.instant('new_entries.you_are_about_to_exclude_this_sentence'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('general.yes'),
      cancelButtonText: this.translate.instant('general.no'),
      confirmButtonColor: '#087f23',
      cancelButtonColor: '#c62828',
    }).then((result: any) => {
      if (result.isConfirmed) {
        this.spinnerService.show();
        this.reportService.toggleWhiteCharacterSentences(this.data.submissionId, id, !this.data?.manipulations?.[1]?.isExcluded || null)
          .pipe()
          .subscribe((res: any) => {
            if (res) {
              this.reportComponent.getS3Json(res?.presignedUrlJson);
            }
            this.reportService.sentenceInformation(null, '', null);
            this.toastr.success(this.data?.manipulations?.[1]?.isExcluded ?? this.data?.manipulations?.[0]?.isExcluded ? this.translate.instant('new_entries.sentence_included_successfully') : this.translate.instant('new_entries.sentence_excluded_successfully'));
            this.spinnerService.hide();
          });
      } else if (result.isDismissed) {
        swal.fire(this.data?.manipulations?.[1]?.isExcluded ?? this.data?.manipulations?.[0]?.isExcluded ? this.translate.instant('new_entries.sentence_is_not_included') : this.translate.instant('new_entries.sentence_is_not_excluded'), '', 'info');
      }
    });
  }

  onRadioChange(sourceId: string) {
    this.selectedSource = sourceId;
  }

  changeSource() {
    this.changeSourceClickCount++;
    this.selectedSentenceSources = this.isOriginal ? this.data.secondarySources : this.translatedObj?.secondarySources
    if (this.changeSourceClickCount === 2) {
      //If click count is 2 -> Send the request
      if (this.selectedSource) {
        this.sendChangeSourceRequest(this.data);
      } else {
        this.changeSourceClickCount--;
        this.toastr.info(
          'Please select source from the list within the window.',
          'Source not selected'
        );
      }
    } else {
      //If click count is 1 -> Display the Secondary Sources
      this.isChangeSource = !this.isChangeSource;
      this.showExclude = false;
    }
  }

  sendChangeSourceRequest(sentence: any) {
    const language = this.isOriginalLanguage ? 'original' : this.toggledLang
    this.spinnerService.show();
    this.reportService
      .changeSentenceSource(
        this.selectedSource,
        parseInt(sentence.id),
        this.data.submissionId,
        language
      )
      .subscribe((data: any) => {
        this.reportService.newS3JsonSubject.next(data[0]);
        this.toastr.success(
          'Sources has been changed successfully',
          'Source changed'
        );
        this.clearSentenceInformation()
        this.spinnerService.hide();
      });
  }

  toggleRow(lang: string): void {
    if (this.toggledLang === lang) {
        this.toggledLang = null;
        this.translatedObj = null;
    } else {
        this.toggledLang = lang;
        const translationObj = this.data.translated.find(obj => obj.lang === lang);
        if (translationObj) {
            this.translatedObj = { ...translationObj };
            this.excludeButtonText = this.translatedObj.isExcluded ?
                this.translate.instant('new_entries.include_matching_sentence') :
                this.translate.instant('new_entries.exclude_matching_sentence');
        }
        this.activeTab('translated');
    }
    this.reportService.selectedTransltaionCodeSubject.next(lang);
  }

  openManipulationTab(tab: string) {
    if (tab === 'charReplacement') {
      this.isOnCharReplacement = true;
      this.isOnHiddenText = false;
    } else if (tab === 'hiddenText') {
      this.formattedText = this.formatTextWithWhiteCharacters(this.data.text, this.data?.manipulations?.[1]?.characters ?? this.data?.manipulations?.[0]?.characters);
      this.isOnCharReplacement = false;
      this.isOnHiddenText = true;
    }
  }

  changeSourceModal(lang) {
    // console.log('lang', lang)
  }

  focusTitle() {
    const titleElement = document.getElementById('sentence-info-title');
    if (titleElement) {
      titleElement.focus();
    }
  }

  toggleProjectSynapseSections() {
    const submissionId = this.acRoute.snapshot.params['id'];

    this.stylometricState.toggleProjectSynapseSections(submissionId, this.data.model_name, this.data.section_key)
      .subscribe((data: any) => {
        this.toastr.success('Section toggled successfully')
        // Read the presigned url and get the json
        this.reportService.getS3Json(data.presignedUrl).subscribe((responseData: any) => {
          if (responseData && responseData.comparison) {
            // Update the section exclusion status in the current data
            const sectionKey = this.data.section_key;
            if (responseData.comparison[sectionKey]) {
              const sectionData = responseData.comparison[sectionKey];

              // Update the local data with the new excluded status
              this.data.isExcluded = sectionData.isExcluded;

              // Update any stylometric data if present
              if (sectionData.stylometrics) {
                this.data.stylometrics = sectionData.stylometrics;
              }

              // Update similarity percentage
              if (sectionData.similarity_percentage !== undefined) {
                this.data.similarity_percentage = sectionData.similarity_percentage;
              }

              // Update AI percentage
              if (sectionData.ai_percentage !== undefined) {
                this.data.ai_percentage = sectionData.ai_percentage;
              }

              // Force change detection to refresh this component
              this.cdr.detectChanges();

              // Update the selected AI model in the stylometric state service
              // This will update the projectSynapseComparison in the document component
              const currentModel = this.stylometricState.getCurrentAIModel();
              if (currentModel && currentModel.comparisonData) {
                // Create a new comparison object with the updated section data
                const updatedComparison = {
                  ...currentModel.comparisonData.comparison,
                  [sectionKey]: sectionData
                };

                // Update the model data
                const updatedModel = {
                  ...currentModel,
                  comparisonData: {
                    ...currentModel.comparisonData,
                    comparison: updatedComparison
                  }
                };

                // Update the model in the service - this will trigger the document component to update
                this.stylometricState.setSelectedAIModel(updatedModel);

                // Emit an event to notify document component to update all highlights for this section
                // Include keepActive flag to ensure the section remains active after toggling
                this.reportService.sectionUpdateSubject.next({
                  type: 'sectionExclusionToggled',
                  sectionKey: sectionKey,
                  isExcluded: sectionData.isExcluded,
                  keepActive: true // Ensure the section stays active after toggling
                });
              }

              // Also notify the report component about the data change
              this.reportService.newS3JsonSubject.next(data.presignedUrl);
            }
          }
        });
      });
  }

  /**
   * Toggles the stylometric table expansion state
   */
  toggleStylometricTable(): void {
    this.isStylometricTableExpanded = !this.isStylometricTableExpanded;
  }

  /**
   * Selects a stylometric category for display
   */
  selectCategory(categoryId: string): void {
    this.selectedCategory = categoryId;
    // Force change detection to update the UI with the new category
    this.cdr.detectChanges();
  }

  /**
   * Gets metrics for the selected category with improved error handling
   */
  getMetricsForCategory(categoryId: string): {name: string, value: number}[] {
    if (!this.data?.stylometrics?.original?.features?.[categoryId] ||
        !this.data?.stylometrics?.ai_generated?.features?.[categoryId]) {
      return [];
    }

    const originalFeatures = this.data.stylometrics.original.features[categoryId];
    const aiFeatures = this.data.stylometrics.ai_generated.features[categoryId];

    return Object.keys(originalFeatures).map(key => {
      const originalValue = originalFeatures[key];
      const aiValue = aiFeatures[key];

      // Calculate the difference percentage for highlighting
      const difference = Math.abs(originalValue - aiValue);
      const average = (originalValue + aiValue) / 2;
      const percentDifference = difference / average;

      return {
        name: key,
        value: originalValue,
        percentDifference: percentDifference,
        threshold: this.stylometricThresholds[categoryId]
      };
    });
  }

  /**
   * Checks if a specific feature is flagged in the stylometric analysis
   */
  isFlaggedFeature(category: string, metricName: string): boolean {
    if (!this.data?.stylometrics?.flagged_features) {
      return false;
    }

    const featureKey = `${category}.${metricName}`;
    return this.data.stylometrics.flagged_features.includes(featureKey);
  }

  /**
   * Gets the CSS class for a metric value based on whether it's flagged
   */
  getMetricValueClass(category: string, metricName: string): string {
    if (this.isFlaggedFeature(category, metricName)) {
      return 'flagged-feature';
    }
    return '';
  }

  /**
   * Gets AI-generated value for a specific metric with styling
   */
  getAIMetricValue(category: string, metricName: string): string {
    const value = this.data?.stylometrics?.ai_generated?.features?.[category]?.[metricName];
    return value !== undefined ? value.toFixed(4) : 'N/A';
  }

  /**
   * Gets the original text value for a specific metric with styling
   */
  getOriginalMetricValue(category: string, metricName: string): string {
    const value = this.data?.stylometrics?.original?.features?.[category]?.[metricName];
    return value !== undefined ? value.toFixed(4) : 'N/A';
  }

  viewSideBySideComparison() {
    // Signal to the document component to enable two columns mode
    this.stylometricState.toggleProjectSynapseActive(true);

    // Just enable side-by-side view - let the document component handle the PDF URL
    this.twoColumns = true;
    this.isSideBySideVisible = true;

    // Update the shared subject to notify document component
    this.reportService.isSideBySideVisibleSubject.next(true);

    // No need to set PDF URL here - document component will determine it
  }

  closeSideBySideComparison() {
    this.isSideBySideVisible = false;
    this.twoColumns = false;

    // Update the shared subject to notify document component
    this.reportService.isSideBySideVisibleSubject.next(false);
  }

  /**
   * Gets the total count of stylometric features being analyzed
   */
  getTotalFeatureCount(): number {
    let count = 0;
    const categories = ['lexical', 'syntactic', 'structural', 'readability'];

    if (this.data?.stylometrics?.original?.features) {
      for (const category of categories) {
        count += Object.keys(this.data.stylometrics.original.features[category] || {}).length;
      }
    }

    return count;
  }
  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.subscriptions = [];
  }

  toggleSourceHistory(): void {
    this.isSourceHistoryExpanded = !this.isSourceHistoryExpanded;
  }

  formatAIModel(model) {
    const modelNames = {
      'gpt-3': 'OpenAI\'s GPT-3',
      'gpt-4': 'OpenAI\'s GPT-4',
      'gpt-4-turbo': 'OpenAI\'s GPT-4 Turbo',
      'gpt-4o': 'OpenAI\'s GPT-4o',
      'gpt-4o-mini': 'OpenAI\'s GPT-4o-mini',
      'gpt-o1-preview': 'OpenAI\'s O1 Preview',
      'gpt-3.5-turbo': 'OpenAI\'s GPT-3.5 Turbo',
      'gpt-3.5-turbo-16k': 'OpenAI\'s GPT-3.5 Turbo 16k',
      'claude-3-5-sonnet': 'Anthropic\'s Claude 3.5 Sonnet',
      'claude-3-opus': 'Anthropic\'s Claude 3 Opus',
      'claude-3-haiku': 'Anthropic\'s Claude 3 Haiku',
      'claude-3-7-sonnet': 'Anthropic\'s Claude 3.7 Sonnet',
      'claude-3-7-sonnet-thinking': 'Anthropic\'s Claude 3.7 Sonnet Reasoning Model',
      'deepseek-r1-distill-qwen-32b': 'Deepseek R1 Distill Qwen 32B',
      'gemma2-9b-it': 'Google\'s Gemma 2 9B-IT',
      'llama-3.3-70b': 'Meta\'s Llama 3.3 70B',
      'qwen-awq-32b': 'Qwen AWQ 32B',
      'qwen-qwq-32b': 'Qwen QWQ 32B',
    }
    return modelNames[model] || model;
  }

  /**
   * Force refresh the AI text display by temporarily removing and re-adding the text
   */
  private forceRefreshAIText(): void {
    if (!this.data) return;

    // Store the current AI text
    const currentText = this.data.ai_generated;

    // Temporarily clear the text
    this.data.ai_generated = '';

    // Force change detection
    this.cdr.detectChanges();

    // Put the text back
    setTimeout(() => {
      this.data.ai_generated = currentText;

      // Force change detection again
      this.cdr.detectChanges();
    }, 0);
  }

  onModelChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedModelValue = target.value;


    // Find the selected model object from available models
    const selectedModel = this.availableAIModels.find(model => model.value === selectedModelValue);

    if (selectedModel && selectedModel.comparisonData) {
      // Update the current model in this component
      this.currentAIModel = selectedModel.value;

      // Get the current section key from the data
      const sectionKey = this.data?.section_key;

      if (sectionKey && selectedModel.comparisonData.comparison &&
          selectedModel.comparisonData.comparison[sectionKey]) {

        // Direct access to the AI generated text from the comparison data
        const comparisonData = selectedModel.comparisonData.comparison[sectionKey];

        if (this.data) {
          // Directly set the AI generated text
          this.data.ai_generated = comparisonData.ai_generated;
          // Also update other metrics if available
          if (comparisonData.similarity_percentage !== undefined) {
            this.data.similarity_percentage = comparisonData.similarity_percentage;
          }

          if (comparisonData.ai_percentage !== undefined) {
            this.data.ai_percentage = comparisonData.ai_percentage;
          }

          // Update complete_percentage from the model's comparisonData.ai_percentage
          if (selectedModel.comparisonData.ai_percentage !== undefined) {
            this.data.complete_percentage = selectedModel.comparisonData.ai_percentage;
          }

          if (comparisonData.stylometrics) {
            // Preserve existing flagged_features from backend if they exist
            const existingFlaggedFeatures = comparisonData.stylometrics.flagged_features;

            this.data.stylometrics = comparisonData.stylometrics;

            // Only calculate flagged features if they don't exist or are empty
            if (!existingFlaggedFeatures || existingFlaggedFeatures.length === 0) {
              this.calculateFlaggedFeatures();
            }
          }
        }
      } else {
        console.warn('No comparison data found for section:', sectionKey);

        // If no section-specific data, try to get complete_percentage from model level
        if (this.data && selectedModel.comparisonData.ai_percentage !== undefined) {
          this.data.complete_percentage = selectedModel.comparisonData.ai_percentage;
        }
      }

      // Update the model in the shared state service with a flag to prevent re-highlighting
      this.stylometricState.setSelectedAIModel(selectedModel, true);

      // Force change detection to update the UI immediately
      this.cdr.detectChanges();

      // Force refresh the AI text display
      this.forceRefreshAIText();

      // Double-check that the text was updated
    }
  }

  /**
   * Updates the data with the current AI model when in Project Synapse mode
   */
  private updateDataWithCurrentModel(): void {
    if (!this.isProjectSynapseActive || !this.data) return;

    const selectedModel = this.availableAIModels.find(model => model.value === this.currentAIModel);
    if (selectedModel && selectedModel.comparisonData) {
      const aiData = selectedModel.comparisonData;
      const sectionKey = this.data.section_key;

      if (sectionKey && aiData.comparison && aiData.comparison[sectionKey]) {
        const comparisonData = aiData.comparison[sectionKey];

        // Update AI generated text
        if (comparisonData.ai_generated !== undefined) {
          this.data.ai_generated = comparisonData.ai_generated;
        } else if (comparisonData.text !== undefined) {
          this.data.ai_generated = comparisonData.text;
        } else if (comparisonData.generated_text !== undefined) {
          this.data.ai_generated = comparisonData.generated_text;
        }

        // Update metrics
        if (comparisonData.similarity_percentage !== undefined) {
          this.data.similarity_percentage = comparisonData.similarity_percentage;
        }

        if (comparisonData.ai_percentage !== undefined) {
          this.data.ai_percentage = comparisonData.ai_percentage;
        }

        // Update complete_percentage from the model's comparisonData.ai_percentage
        if (selectedModel.comparisonData.ai_percentage !== undefined) {
          this.data.complete_percentage = selectedModel.comparisonData.ai_percentage;
        }

        // Update stylometrics
        if (comparisonData.stylometrics) {
          // Preserve existing flagged_features from backend if they exist
          const existingFlaggedFeatures = comparisonData.stylometrics.flagged_features;

          this.data.stylometrics = comparisonData.stylometrics;

          // Only calculate flagged features if they don't exist or are empty
          if (!existingFlaggedFeatures || existingFlaggedFeatures.length === 0) {
            this.calculateFlaggedFeatures();
          }
        }

        // Force change detection
        this.cdr.detectChanges();
      } else {
        // If no section-specific data, still try to update complete_percentage from model level
        if (selectedModel.comparisonData.ai_percentage !== undefined) {
          this.data.complete_percentage = selectedModel.comparisonData.ai_percentage;
        }
      }
    }
  }

  /**
   * Handles data changes to ensure proper model syncing
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Check if we have data changes and are in Project Synapse mode
    if (changes.data && this.isProjectSynapseActive && this.data) {
      // Ensure we have the current model from the service
      const currentModel = this.stylometricState.getCurrentAIModel();
      if (currentModel) {
        this.currentAIModel = currentModel.value;
        this.updateDataWithCurrentModel();
      }
    }
  }

  /**
   * Initialize the stylometric table with default values and settings
   */
  private initializeStylometricTable(): void {
    // Set default category if not already set
    if (!this.selectedCategory) {
      this.selectedCategory = 'lexical';
    }

    // Ensure we have the stylometric data structure with proper initialization
    if (!this.data.stylometrics) {
      this.data.stylometrics = {
        original: {
          features: {
            lexical: {},
            syntactic: {},
            structural: {},
            readability: {}
          }
        },
        ai_generated: {
          features: {
            lexical: {},
            syntactic: {},
            structural: {},
            readability: {}
          }
        },
        flagged_features: [],
        stylistic_discrepancy: ''
      };
    }

    // Initialize thresholds for feature flagging if not set
    this.stylometricThresholds = {
      lexical: 0.15,    // 15% difference threshold for lexical features
      syntactic: 0.20,  // 20% difference threshold for syntactic features
      structural: 0.25, // 25% difference threshold for structural features
      readability: 0.30 // 30% difference threshold for readability features
    };

    // Only calculate flagged features if they don't exist or are empty from backend
    if (!this.data.stylometrics.flagged_features || this.data.stylometrics.flagged_features.length === 0) {
      this.calculateFlaggedFeatures();
    }
  }

  /**
   * Calculate which features should be flagged based on thresholds
   */
  private calculateFlaggedFeatures(): void {
    if (!this.data?.stylometrics?.original?.features || !this.data?.stylometrics?.ai_generated?.features) {
      return;
    }

    const flaggedFeatures: string[] = [];

    // Check each category
    Object.keys(this.stylometricThresholds).forEach(category => {
      const originalFeatures = this.data.stylometrics.original.features[category];
      const aiFeatures = this.data.stylometrics.ai_generated.features[category];
      const threshold = this.stylometricThresholds[category];

      if (originalFeatures && aiFeatures) {
        Object.keys(originalFeatures).forEach(metric => {
          const originalValue = originalFeatures[metric];
          const aiValue = aiFeatures[metric];

          if (originalValue !== undefined && aiValue !== undefined) {
            // Calculate percentage difference
            const difference = Math.abs(originalValue - aiValue);
            const average = (originalValue + aiValue) / 2;
            const percentDifference = difference / average;

            if (percentDifference > threshold) {
              flaggedFeatures.push(`${category}_${metric}`);
            }
          }
        });
      }
    });

    // Update the flagged features in the data
    this.data.stylometrics.flagged_features = flaggedFeatures;
  }

  /**
   * Safely checks if the current source is excluded
   */
  isSourceExcluded(): boolean {
    if (!this.data) return false;

    if (this.isOriginal) {
      return this.data.source?.isExcluded || false;
    } else if (this.isTranslated && this.translatedObj) {
      return this.translatedObj.source?.isExcluded || false;
    }

    return false;
  }

  shouldShowStylometrics(threshold: number = 95): boolean {
    if (!this.data || !this.isProjectSynapseActive) {
      return true; // Show stylometrics by default for non-Project Synapse modes
    }

    // If we have complete_percentage, use it to determine visibility
    if (this.data.complete_percentage !== undefined && this.data.complete_percentage !== null) {
      return this.data.complete_percentage < threshold;
    }

    // Fallback to ai_percentage if complete_percentage is not available
    if (this.data.ai_percentage !== undefined && this.data.ai_percentage !== null) {
      return this.data.ai_percentage < threshold;
    }

    // Default to showing stylometrics if no percentage data is available
    return true;
  }
}
